import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { AdmissionStatusEnum, isShuttleOffer, TrainTypeEnum, TripTypeEnum, SHUTTLE_PRODUCT_CODE } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBooking, IBookingResponse, StationsListType } from "../../../Orchestrator/Types/Index";
import { IGetBookingDetailsActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookingDetailActionParams";
import { showNotification } from "../../../Services/notificationService";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { probeRefundOffersAction } from "../Addons/ProbeRefundOffersAction";
import { ticketFulfillmentIdsForNolLegs } from "../../../Components/OnwardTravel/nolTicketRefund";
import { getListOfServiceRequestAction } from "../ServiceRequestDetails/GetListOfServiceRequestAction";
import { getUpgradePremiumOfferAction } from "../UpgradePremium/GetUpgradePremiumOfferAction";
import { requestUpgradeOffersForBooking } from "../UpgradePremium/UpgradePremiumHelper";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { PassengerPrmNeedTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getStops } from "../../../app/api/getStops";
import { bookingsPastPivotDubai, loadBookingAppearanceMinutes } from "../../../Lib/Utils/bookingAppearancePeriod";
import { getRuntimeConfig } from "../../../Lib/Config/RuntimeConfig";
import { transformStops } from "../Search/GetStopsAction";
import { isNolAdmission, nolOffersForLegs } from "../../../Lib/Utils/nolCard";
import { pairSupersededLegs, supersededTripsOf, type IDisplaySupersededLegs } from "../../../Components/OnwardTravel/bookedNolForLeg";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Admission statuses meaning the passenger PAID for this shuttle at some point.
 *
 * An onward-travel shuttle is its own train-less trip and is never listed in a
 * booking card's `tripIds` (those hold the outbound/inbound TRAIN trips only —
 * GetBookingToRightDataAction), so it survives the passenger-scoped filter below
 * on the strength of its admissions alone. Cancelling it from "Manage
 * End-to-end Journey" leaves the offer and the trip on the booking but moves the
 * admissions from FULFILLED to RELEASED/REFUNDED, so a FULFILLED-only gate
 * dropped the trip — and with it the offer, so PrepareBookingSummaryAction never
 * saw a bus leg and could not draw its "<n> Shuttle <stop> (Cancelled)" row. The
 * shuttle simply vanished from the receipt instead of reading as cancelled.
 *
 * PREBOOKED is deliberately absent: an addition abandoned at payment was never
 * paid for, and the summary already drops all-PREBOOKED offers on this surface.
 *
 * Keep the refunded half in step with `REFUNDED_ADMISSION_STATUSES` in
 * PrepareBookingSummaryAction: a status this set keeps the trip for but that one
 * does not draw a row for buys the summary an invisible shuttle. Not imported
 * from there — that module's test mocks are documented as fragile (see its note
 * on inlining `hasTrainLeg`), and two lines are cheaper than six mock edits.
 */
/** FULFILLED, and the backend's long-standing misspelling of it. */
const CARRIED_ADMISSION_STATUSES = new Set<string>([
    AdmissionStatusEnum.FULFILLED,
    // Not on the enum, deliberately: it is a backend typo, not a status. Sibling
    // code accepts both (PrepareBookingSummaryAction's settled-items scan,
    // mergeUpgradePaymentSummary), and a card whose booking spells it this way
    // must not lose its shuttle.
    "FULLFILLED",
]);

const PAID_OR_REFUNDED_ADMISSION_STATUSES = new Set<string>([
    ...CARRIED_ADMISSION_STATUSES,
    AdmissionStatusEnum.REFUNDED,
    AdmissionStatusEnum.RELEASED,
]);

/**
 * Admission states meaning "this trip was replaced by another one" — the
 * signature of the trip a change-date moved the passenger OFF.
 *
 * EXCHANGE_ONGOING as well as EXCHANGED: the exchange is only EXCHANGED once
 * the replacement settles, and the card has to keep showing the pass that was
 * cancelled alongside it in the window before that.
 */
const SUPERSEDED_ADMISSION_STATUSES = new Set<string>([
    AdmissionStatusEnum.EXCHANGED,
    AdmissionStatusEnum.EXCHANGE_ONGOING,
]);

/**
 * An onward-travel shuttle trip: every leg is a bus.
 *
 * The clause this replaced tested `legs.length === 1`, which would drop a
 * two-hop bus-only trip out of both the keep and the carve-out below. Stated the
 * way the rest of the codebase states it (GetBookingAction, TicketDetail's
 * GetBookingDetailAction) — a train+bus trip is still not one of these.
 */
export const isShuttleOnlyTrip = (trip: any): boolean =>
    Array.isArray(trip?.legs) &&
    trip.legs.length > 0 &&
    trip.legs.every((leg: any) => leg?.timedLeg?.service?.mode?.ptMode === "BUS");

/**
 * Keep this card's onward shuttle trip, which its `tripIds` never names.
 *
 * Exported for the regression test — the whole defect lives in which statuses
 * count here, and the action around it cannot be exercised without a booking
 * fetch.
 */
export const shouldKeepShuttleTrip = (
    trip: any,
    bookedOffers: any[],
    passengerIdsForTrip: Set<string>,
    /**
     * The trips this card actually names. A shuttle is kept only when it serves
     * one of them.
     *
     * Passenger match alone is not enough: on a single-passenger round trip the
     * traveller owns BOTH shuttles, so every card re-admitted both and each
     * leg's Booking summary billed two of them — AED 95 + 95 against 170 paid,
     * with the surplus row orphaned above the station heading (UR5SK2). Omit to
     * keep the old passenger-only behaviour for callers that show the whole
     * booking and therefore want every shuttle.
     */
    namedTrips?: any[],
    allTrips?: any[]
): boolean => {
    if (!isShuttleOnlyTrip(trip)) return false;
    if (passengerIdsForTrip.size === 0) return false;
    // Anchor on the leg each named trip SERVES, not its stamped `direction`. A
    // card can legitimately name a shuttle (the surviving leg's onward hop after
    // the other leg was cancelled), and every shuttle is stamped OUT_BOUND — so
    // matching on the label let that shuttle vouch for the CANCELLED leg's
    // shuttle, which came back as an orphaned row on the card (UR5SK2).
    // `namedTrips?.length`, not `namedTrips`: an EMPTY array is truthy, and
    // `![].some(...)` is `true` — which would reject every shuttle and quietly
    // reinstate the defect this scoping exists to fix. No named trips means no
    // scoping to apply.
    if (namedTrips?.length && !namedTrips.some((named) => {
        // canonicalDirection: `onwardTripDirection` returns a TRAIN's own
        // `direction` verbatim, and the backend has been seen spelling it
        // "OUTBOUND". Comparing that raw against a canonicalised result matches
        // nothing, which would reject EVERY shuttle from every card and lose the
        // shuttle row (and its money) from booking-details.
        const raw = onwardTripDirection(named, (allTrips || namedTrips) as any);
        return !!raw && shuttleServesDirection(trip, allTrips || namedTrips, canonicalDirection(raw));
    })) return false;
    return (bookedOffers || []).some((offer: any) =>
        offer?.tripCoverage?.coveredTripId === trip.id &&
        Array.isArray(offer.admissions) &&
        offer.admissions.some(
            (a: any) =>
                PAID_OR_REFUNDED_ADMISSION_STATUSES.has(a?.status) &&
                Array.isArray(a.passengerIds) &&
                a.passengerIds.some((pid: string) => passengerIdsForTrip.has(pid))
        )
    );
};

/**
 * Does this admission survive the partial-passenger view (a CDT split or
 * partial-cancel card, which shows only what its own passengers still hold)?
 *
 * FULFILLED alone for everything the card names — but a shuttle-only trip is
 * kept by `shouldKeepShuttleTrip` precisely BECAUSE it carries this card's
 * shuttle, cancelled or not. Applying the FULFILLED-only rule to it would strip
 * the offer empty again and undo the row the trip was kept for, so the two
 * predicates have to agree on which statuses count.
 *
 * Exported for the same reason as `shouldKeepShuttleTrip`: this is the half of
 * the fix that is easiest to regress and it is otherwise buried inline.
 */
export const isAdmissionKeptInPartialView = (
    admission: any,
    coveredTripId: string | undefined,
    keptShuttleTripIds: Set<string>
): boolean =>
    CARRIED_ADMISSION_STATUSES.has(admission?.status) ||
    // A Nol pass sits on its OWN booked offer, which carries NO tripCoverage —
    // so the shuttle carve-out below can never reach it, whatever the card. A
    // pass cancelled by a change-date or a partial cancel was therefore stripped
    // here, its offer emptied, and the offer then dropped by the length check
    // downstream: the pass vanished from Ticket Summary altogether rather than
    // showing as cancelled (ACPT-012 / ABDA-18833).
    //
    // Scope is already established for these: `nolOfferIds` admits a pass offer
    // only when it covers one of this card's legs, and `passengerMatch` beside
    // this call still requires the admission to name one of its passengers.
    (isNolAdmission(admission) &&
        PAID_OR_REFUNDED_ADMISSION_STATUSES.has(admission?.status)) ||
    (!!coveredTripId &&
        keptShuttleTripIds.has(coveredTripId) &&
        PAID_OR_REFUNDED_ADMISSION_STATUSES.has(admission?.status));

const shouldRequestUpgrade = (booking: any): boolean => {
    if (!booking) return false;
    const trips: any[] = booking?.trips || [];
    const bookedOffers: any[] = booking?.bookedOffers || [];
    const passengers: any[] = booking?.passengers || [];

    if (trips.length === 0) return false;
    const earliestStart = trips
        .map((t: any) => t?.startTime)
        .filter(Boolean)
        .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())[0];
    if (!earliestStart) return false;
    const tripDateDubai = dayjs(earliestStart).tz("Asia/Dubai");
    const nowDubai = dayjs().tz("Asia/Dubai");
    const isUpcoming = tripDateDubai.isAfter(nowDubai);
    if (!isUpcoming) return false;

    // A cancelled ONWARD SHUTTLE says nothing about whether the train can be
    // upgraded, and it is now retained on this card (see shouldKeepShuttleTrip)
    // so the "(Cancelled)" row can draw. Without this exclusion that retention
    // silently took the Upgrade CTA with it: the shuttle offer's admissions are
    // all RELEASED, which is exactly what this predicate treats as "the booking
    // has been cancelled, don't offer an upgrade".
    const hasRefundedOrReleased = (bookedOffers || []).some((offer: any) =>
        !isShuttleOffer(offer) &&
        // `[].every()` is true, so an offer with NO admissions used to read as
        // "entirely refunded" and kill the CTA. Nol offers carry no admissions
        // array at all (see PrepareBookingSummaryAction's notes on the same
        // shape) and are not shuttle offers, so the exclusion above misses them.
        (offer?.admissions?.length ?? 0) > 0 &&
        offer.admissions.every((adm: any) => adm?.status === "REFUNDED" || adm?.status === "RELEASED")
    );
    if (hasRefundedOrReleased) return false;

    const noWheelchairPax = (passengers || []).every((p: any) => !(p?.prmNeeds || []).includes(PassengerPrmNeedTypeEnum.Wheelchair));
    if (!noWheelchairPax) return false;

    return true;
}

const hasOnlyShuttleLegs = (booking: any): boolean => {
    if (!booking) return false;
    const bookedOffers: any[] = booking?.bookedOffers || [];
    
    if (bookedOffers?.length === 0) return false;
    
    const allOffersAreShuttle = bookedOffers?.every((offer: any) => {
        const products = offer?.products || [];
        if (products?.length === 0) return false;
        return products?.every((product: any) => product?.code === SHUTTLE_PRODUCT_CODE);
    });
    
    return allOffersAreShuttle;
}

const areAllPassengersInLatestTripsAlreadyUpgraded = (booking: any): boolean => {
    if (!booking) return false;
    const bookedOffers: any[] = booking?.bookedOffers || [];
    const trips: any[] = booking?.trips || [];
    
    if (bookedOffers?.length === 0 || trips?.length === 0) return false;
    
    const latestOutboundTrip = [...trips]?.reverse()?.find(trip => trip?.direction === TripTypeEnum.OUT_BOUND);
    const latestInboundTrip = [...trips]?.reverse()?.find(trip => trip?.direction === TripTypeEnum.IN_BOUND);
    
    const latestTrips = [latestOutboundTrip, latestInboundTrip].filter(Boolean);
    
    if (latestTrips.length === 0) return false;
    
    const latestTripIds = latestTrips?.map(trip => trip.id);
    const latestOffers = bookedOffers?.filter(offer => 
        latestTripIds.includes(offer.tripCoverage?.coveredTripId)
    );
    
    const activeLatestOffers = latestOffers?.filter(offer => {
        const admissions = offer?.admissions || [];
        return !admissions.every((adm: any) => adm?.status === "EXCHANGED");
    });
    
    if (activeLatestOffers?.length === 0) return false;
    
    const trainOffers = activeLatestOffers?.filter((offer: any) => {
        const products = offer?.products || [];
        const productCode = products[0]?.code;
        return productCode !== SHUTTLE_PRODUCT_CODE;
    });
    
    if (trainOffers?.length === 0) {    
        const hasExchangeFees = activeLatestOffers?.some((offer: any) => {
            const fees = offer?.fees || [];
            return fees.some((fee: any) => 
                fee?.type === "MODIFICATION_FEE" || 
                fee?.type === "EXCHANGE_FEE" ||
                fee?.summary?.toLowerCase().includes("exchange")
            );
        });
        
        const allOffersForLatestTrips = bookedOffers?.filter(offer => 
            latestTripIds.includes(offer.tripCoverage?.coveredTripId)
        );
        const hasExchangedAdmissions = allOffersForLatestTrips?.some((offer: any) => {
            const admissions = offer?.admissions || [];
            return admissions.some((adm: any) => adm?.status === "EXCHANGED");
        });
        
        return hasExchangeFees || hasExchangedAdmissions;
    }
    
    const allTrainOffersArePremium = trainOffers?.every((offer: any) => {
        const products = offer?.products || [];
        return products.some((product: any) => product?.code === "PFF");
    });
    
    return allTrainOffersArePremium;
}
import { getBookedCommentsAction } from "./getBookedCommentsAction";
import { getBookingJourneyDetailAction } from "./GetBookingJourneyDetailAction";
import { getBookingPaymentDataAction } from "./GetBookingPaymentDataAction";
import { cancelledLegTripIds, isBookingMultiBooking, preferTrainTrips } from "../../../Lib/Utils/booking";
import { canonicalDirection, onwardTripDirection, shuttleServesDirection } from "../../../Lib/Utils/tripDirectionRecovery";

class GetBookingDetailDataAction extends BaseAction<void, IGetBookingDetailsActionParams> {

    override async run(): Promise<void> {
        try {
            // Settle the appearance period before the split decision below peeks
            // it. A deep link or refresh straight onto this page reaches here
            // without the bookings list having run, so nothing else has warmed
            // the read. Session-cached, so this is a no-op after the first call.
            await loadBookingAppearanceMinutes();

            const { bookingId, direction, displayPassengerData, selectedTripsData, isManageSeating = false, isAnonymous = false } = this.params!;

            // Viewing booking-details means the user has navigated AWAY
            // from any active manage-seating / addons session — even if
            // they didn't pay. Clear both the pending-refund queue and
            // the refund-quote cache so the booking summary doesn't
            // render a stale [Refund] badge on a FULFILLED ancillary the
            // user only tentatively deselected. If they re-enter
            // manage-seating they'll re-queue and re-probe from scratch.
            updateRecoilState("pendingRefundFulfillmentIdsAtom", null);
            updateRecoilState("refundOffersByFulfillmentIdAtom", null);

            if (bookingId) {
                const response: any = await getBooking(bookingId);

                const stopResponse: any = await getStops();
                const stops = stopResponse.status === "ok" ? stopResponse.answer.stops : [];
                if (stops.length > 0) {
                    const config = await getRuntimeConfig();
                    let StationLists = transformStops(stops, config.NEXT_PUBLIC_STOP_EXTERNAL_CODE)
                    updateRecoilState<StationsListType>("stopsResponseAtom", StationLists);
                }
                
                //Get Booking Payment Data API.
                getBookingPaymentDataAction.setParams({ bookingId: bookingId });
                getBookingPaymentDataAction.execute();

                getBookedCommentsAction.setParams({ bookingId })
                await getBookedCommentsAction.execute();
                if (getBookedCommentsAction.status.executionStatus === ExecutionStatus.SUCCESS && getBookedCommentsAction.result) {
                    getBookingJourneyDetailAction.setParams({ bookingComments: getBookedCommentsAction.result.items })
                    await getBookingJourneyDetailAction.execute();
                }

                if (response.status === 'ok' && response.answer) {
                    let bookingResponse: IBookingResponse = response.answer;
                    const { booking } = bookingResponse;

                    /**
                     * ---------------------------------------------------------------
                     * This below logic is to check that booking is modified or not.
                     * This is required to check for book it again.
                     * If booking is modified then no need to show book it again
                     * Booking modified means, Cancel, Exchange, Date change etc.
                    */
                    let noModificationOnBooking: boolean = true
                    for (let i = 0; i < booking.bookedOffers.length; i++) {
                        const bookAdmissions = booking.bookedOffers[i].admissions;
                        const isAnyNotFulfilled = bookAdmissions.find(d => d.status !== "FULFILLED")
                        if (isAnyNotFulfilled) {
                            noModificationOnBooking = false
                            break;
                        }
                    }
                    bookingResponse = {
                        ...bookingResponse,
                        booking: {
                            ...bookingResponse.booking,
                            noModificationOnBooking: noModificationOnBooking
                        }
                    }
                    /*---------------------------------------------------------------*/

                    const isMultiBookingDetailFlow = isBookingMultiBooking(bookingResponse)
                    if (isMultiBookingDetailFlow) {
                        updateRecoilState('bookingResponseAtom', bookingResponse);
                        prepareBookingSummaryAction.setParams({ ...bookingResponse.booking, isManageSeating: isManageSeating, isFrom: "booking-details" });
                        await prepareBookingSummaryAction.execute();
                    } else if (selectedTripsData?.tripIds && selectedTripsData?.tripIds?.length > 0 && selectedTripsData?.passengerData) {
                        // Passenger-scoped filtering path — takes priority over direction-only filtering.
                        // This handles CDT partial-passenger splits, partial cancellations, and other
                        // after-sales scenarios where each card targets specific trips + passengers.
                        const allTrips = bookingResponse.booking?.trips || [];
                        const allBookedOffers = bookingResponse.booking?.bookedOffers || [];

                        const outboundPassengerIds = new Set<string>(selectedTripsData?.passengerData?.outbound || []);
                        const inboundPassengerIds = new Set<string>(selectedTripsData?.passengerData?.inbound || []);
                        const combinedPassengerIds = new Set<string>([
                            ...outboundPassengerIds,
                            ...inboundPassengerIds,
                        ]);

                        const getPassengerIdsForTrip = (trip: any) => {
                            if (trip.direction === TripTypeEnum.OUT_BOUND) {
                                return outboundPassengerIds;
                            }
                            if (trip.direction === TripTypeEnum.IN_BOUND) {
                                return inboundPassengerIds;
                            }
                            return combinedPassengerIds;
                        };

                        // The trips this card names — a shuttle must serve one of them.
                        const namedTrips = allTrips.filter(trip => selectedTripsData.tripIds.includes(trip.id));

                        const keptTrips = allTrips.filter(
                            trip =>
                                selectedTripsData.tripIds.includes(trip.id) ||
                                // BOTH directions' passengers for a shuttle, never
                                // `getPassengerIdsForTrip`: the backend stamps every
                                // onward shuttle trip OUT_BOUND regardless of which
                                // train it serves (see CalculatePassengerListByPriceAction's
                                // note on the same quirk), so a card scoped to the
                                // inbound direction would look up an empty outbound
                                // set and drop the shuttle — reinstating the exact
                                // defect this retention exists to fix.
                                shouldKeepShuttleTrip(trip, allBookedOffers, combinedPassengerIds, namedTrips, allTrips)
                        );

                        const keptTripIds = keptTrips.map(trip => trip.id);
                        // Only the trips kept BECAUSE of their shuttle — a train trip
                        // named by `tripIds` is not one of these even if it carries a
                        // bus leg, so the partial-view rule below stays untouched for
                        // everything the card actually names.
                        const keptShuttleTripIds = new Set<string>(
                            keptTrips
                                .filter(trip => !selectedTripsData.tripIds.includes(trip.id) && isShuttleOnlyTrip(trip))
                                .map(trip => trip.id)
                        );

                        // Detect partial-passenger view: the card covers fewer passengers
                        // than the full booking. For partial views (CDT split, partial cancel),
                        // filter admissions to FULFILLED only. For full booking views, keep all.
                        const totalBookingPassengers = bookingResponse.booking?.passengers?.length || 0;
                        const isPartialPassengerView = combinedPassengerIds.size > 0 && combinedPassengerIds.size < totalBookingPassengers;

                        // A NOL pass sits on its OWN booked offer, and that offer
                        // carries NO tripCoverage at all — only its ADMISSION knows
                        // which legs it covers. Filtering on `coveredTripId` alone
                        // therefore dropped every pass here, before the summary ran:
                        // the passenger was charged for it and the Booking summary
                        // showed the fare only (INT booking MWBCLP, D60 listed
                        // against a D69 card payment).
                        // The trips this card's passengers were moved OFF — the
                        // change-date's predecessors. A Nol pass that the same
                        // change-date auto-cancelled (sessionRefundLegs.ts) still
                        // names the OLD leg in its `coveredLegIds`, so scoping on
                        // the kept trips alone dropped its offer here and the
                        // cancelled pass never reached the page at all
                        // (ACPT-012 / ABDA-18833).
                        //
                        // Narrow on purpose: same DIRECTION as a trip this card
                        // kept, and named by this card's own passengers. Widening
                        // to "any leg on the booking" would put the inbound's pass
                        // on the outbound card of a round trip.
                        const namesCardPassenger = (ids: string[] | undefined) =>
                            combinedPassengerIds.size === 0 ||
                            (ids || []).some((pid: string) => combinedPassengerIds.has(pid));
                        const keptDirections = new Set<string>(
                            keptTrips.map((trip: any) => trip?.direction).filter(Boolean)
                        );
                        // The one place a superseded trip is scoped to the CARD's
                        // passengers — a trip somebody else exchanged away is not this
                        // card's history. Everything else about "which trips were
                        // superseded" is the shared rule; the passenger scope rides in
                        // as a predicate so this stops being a separate copy of it.
                        //
                        // `keptDirections` is dropped deliberately. Not merely redundant —
                        // MEASURED against the old predicate across ten booking shapes,
                        // three of which diverge, and two of those are FIXES:
                        //
                        //  - a KEPT trip with no `direction` shrank the old
                        //    `new Set(keptTrips.map(t => t?.direction).filter(Boolean))`;
                        //    all of them missing emptied it, and an empty set excluded
                        //    EVERY superseded trip — killing the handoff for that card.
                        //  - a SUPERSEDED trip with no `direction` was silently excluded,
                        //    stranding the pass. Span finds both.
                        //
                        // The one genuine widening needs a superseded trip sharing a kept
                        // trip's exact train span while carrying the OPPOSITE direction
                        // label — the known mistag inverted, never observed, and
                        // geometrically odd on a booking whose outbound already runs it.
                        //
                        // Keeping the scope would also have made this summary STRICTER
                        // than the refund that follows it: the cancel derivation has never
                        // had a direction scope, so a pass could show here and not be
                        // refunded. Display and money disagreeing is the bug class this
                        // whole branch exists to close.
                        const supersededTrips = supersededTripsOf(
                            allTrips,
                            keptTrips,
                            allBookedOffers,
                            namesCardPassenger
                        );

                        const keptLegIds = new Set<string>([
                            ...keptTrips.flatMap((trip: any) => (trip.legs || []).map((leg: any) => leg.id)),
                            ...supersededTrips.flatMap((trip: any) =>
                                (trip.legs || []).map((leg: any) => leg.id)
                            ),
                        ]);
                        const nolOfferIds = new Set(
                            nolOffersForLegs({ bookedOffers: allBookedOffers }, keptLegIds).map(
                                (offer: any) => offer.offerId
                            )
                        );

                        const filteredOffers = allBookedOffers
                            .filter((offer: any) => {
                                const coveredId = offer.tripCoverage?.coveredTripId;
                                return keptTripIds.includes(coveredId) || nolOfferIds.has(offer.offerId);
                            })
                            .map((offer: any) => {
                                if (combinedPassengerIds.size === 0) return offer;
                                const scopedAdmissions = (offer.admissions || []).filter((admission: any) => {
                                    const passengerMatch = admission.passengerIds?.some((pid: string) => combinedPassengerIds.has(pid));
                                    // Only filter to FULFILLED for partial views (CDT, partial cancel),
                                    // with a carve-out for the shuttle trips kept above.
                                    const statusMatch =
                                        !isPartialPassengerView ||
                                        isAdmissionKeptInPartialView(
                                            admission,
                                            offer.tripCoverage?.coveredTripId,
                                            keptShuttleTripIds
                                        );
                                    return passengerMatch && statusMatch;
                                });
                                return { ...offer, admissions: scopedAdmissions };
                            })
                            .filter((offer: any) => (offer.admissions || []).length > 0);

                        // Filter passengers to only those in this card's group
                        const allPassengers = bookingResponse.booking?.passengers || [];
                        const filteredPassengers = combinedPassengerIds.size > 0
                            ? allPassengers.filter((p: any) => combinedPassengerIds.has(p.id))
                            : allPassengers;

                        // The legs each KEPT trip superseded, keyed by that trip's id.
                        //
                        // `trips` below is narrowed to keptTrips, which is right for
                        // every trip-shaped consumer — nobody wants an exchanged-away
                        // journey drawn as a trip card. It is wrong for the one reader
                        // that has to look further back: a Nol pass keeps naming the
                        // leg it was bought against, and Turnit never re-points it
                        // after a change-date or upgrade (verified on INT 2026-09-03).
                        //
                        // The offer survives the filter above via `nolOfferIds`, so the
                        // pass reaches the summary — but with its trip gone the summary
                        // cannot re-derive which legs it may legitimately name, and
                        // dropped the row entirely. Booking QGB756 (two exchanges deep)
                        // read "Total AED 145" against AED 185 taken.
                        //
                        // A MAP, not a flat list, and paired by ENDPOINTS. A flat list
                        // is what every kept trip would borrow from: a booking row
                        // carries both directions, and an End-to-end booking's shuttle
                        // trip sits in the same set as its train. Each such trip then
                        // matched the same pass and the summary added its fare once per
                        // trip — AED 215 where AED 175 was due on a train+shuttle
                        // booking, and the outbound's pass drawn on the inbound card of
                        // a round trip.
                        //
                        // `supersededTrips` is selected by `trip.direction` above, which
                        // over-admits on purpose (the backend tags a return trip
                        // OUT_BOUND like its outbound). `trainTripEndpoints` is the rule
                        // that filters the surplus back out, and it is imported from
                        // bookedNolForLeg rather than rewritten so the two cannot drift.
                        // A bus-only trip yields "" and is paired with nothing.
                        //
                        // Not a wire field — same convention as `noModificationOnBooking`.
                        const displaySupersededLegIdsByTripId =
                            pairSupersededLegs(keptTrips, supersededTrips);

                        // Typed on the WRITE side deliberately: the reader already
                        // names the field, so only a producer-side rename could
                        // silently turn the widening into a no-op.
                        const updatedBooking: IBooking & IDisplaySupersededLegs = {
                            ...bookingResponse.booking,
                            trips: keptTrips,
                            bookedOffers: filteredOffers,
                            passengers: filteredPassengers,
                            displaySupersededLegIdsByTripId,
                            displayIsBookingDetailsView: true
                        };

                        const updatedBookingResponse = {
                            ...bookingResponse,
                            booking: updatedBooking
                        };

                        updateRecoilState('bookingResponseAtom', updatedBookingResponse);

                        // The legs this booking CANCELLED, added to the SUMMARY's input only.
                        //
                        // A bookings row names what its card ACTS on, so `tripsStillHeld` drops a
                        // refunded leg before the list ever builds one, and scoping this page to
                        // those ids alone hid the cancelled half of a partly cancelled round trip
                        // here too: no leg card, no refund row, no record that the customer had
                        // cancelled anything.
                        //
                        // NOT added to `keptTrips`, and the distinction is the whole fix.
                        // `bookingResponseAtom` is this page's ACTION scope, not its record — the
                        // change-date gate (BookingOptions' isChangeDateBlockedByApi), the
                        // departed-leg guard (hasDeparted) and shouldRequestUpgrade below all read
                        // `booking.trips` off it. A trip nobody travels on poisons each one: it is
                        // never probed for exchange offers, so the change-date gate can never
                        // block and a Saver leg the backend refuses gets its menu item back; it
                        // carries a date nobody is travelling on, so hasDeparted stops firing; and
                        // its all-REFUNDED offer is exactly what shouldRequestUpgrade reads as
                        // "this booking was cancelled", which would take the Upgrade CTA off every
                        // partly cancelled booking. The summary is fed directly instead —
                        // `selectedTrainLegsAtom` and the payment rows are both written from these
                        // params, not from the atom.
                        //
                        // Passenger-scoped like every other offer here, but NOT status-scoped:
                        // `isAdmissionKeptInPartialView` keeps FULFILLED only, and a cancelled leg
                        // has nothing FULFILLED left — running it through that filter would empty
                        // the offer and drop the leg again on exactly the CDT-split cards this
                        // clause exists for.
                        //
                        // Deliberately NOT added to `namedTrips`: a cancelled leg's own feeder
                        // shuttle must stay off this card (`shouldKeepShuttleTrip`) — it is the
                        // refunded TRAIN leg that belongs on the summary, not an orphaned bus row
                        // above a station heading it no longer has.
                        //
                        // Minus anything the card already names. The two sets are NOT disjoint
                        // by construction: a card built by `handleRefundedOffer` names a refunded
                        // trip outright while a sibling leg is still live, and its offer is then
                        // in `filteredOffers` already. Concatenating would list that offer twice,
                        // and both the passenger-price flat-map and `payment.refundedAmount` sum
                        // across offers — so the page would show DOUBLE the refund the customer
                        // actually got.
                        const cancelledLegs = cancelledLegTripIds(allTrips, allBookedOffers)
                            .filter((tripId) => !keptTripIds.includes(tripId));
                        const cancelledLegOffers = allBookedOffers
                            .filter((offer: any) => cancelledLegs.includes(offer?.tripCoverage?.coveredTripId))
                            .map((offer: any) => ({
                                ...offer,
                                admissions: (offer.admissions || []).filter((admission: any) =>
                                    namesCardPassenger(admission?.passengerIds)
                                ),
                            }))
                            .filter((offer: any) => (offer.admissions || []).length > 0);

                        // ...and the PASSES bought for those legs, which the clause above cannot
                        // reach. A Nol pass sits on its OWN booked offer carrying no
                        // `tripCoverage` at all (only its admission names `coveredLegIds`), so an
                        // offer filter keyed on `coveredTripId` matches it never — the same reason
                        // `nolOfferIds` exists for the KEPT legs. Cancel the inbound of a round
                        // trip and the leg came back with its fare alone: the refunded pass had no
                        // row and its money was in neither Sub Total nor Refunded Amount, which
                        // read Đ320 / Đ120 against a Đ400 card payment (MDU2FK).
                        //
                        // Deduped against everything already listed, for the reason spelled out
                        // above: `payment.refundedAmount` sums `refundAmount` across offers, so a
                        // pass offer that covers a kept leg AS WELL (already admitted by
                        // `nolOfferIds`, under the same passenger and status scoping) would
                        // otherwise refund twice over. Never a poorer copy either: in a partial
                        // view that clause filters Nol admissions through
                        // `isAdmissionKeptInPartialView`, which applies THIS set; in a full view
                        // it is strictly wider.
                        //
                        // STATUS-scoped, unlike `cancelledLegOffers` above, and the asymmetry is
                        // the point: a cancelled train offer has nothing FULFILLED left, but a
                        // pass offer is not status-homogeneous — it holds one admission per
                        // passenger, and an abandoned onward-travel add leaves PREBOOKED ones
                        // behind. `nolSummaryRowsForTrip` reads PREBOOKED (and ON_HOLD, which
                        // nothing downstream filters) as non-terminal, i.e. a LIVE charge, while
                        // the booking-details drop only discards offers where EVERY admission is
                        // PREBOOKED — so one abandoned pass beside a refunded one on the same
                        // offer would be billed on the receipt, and manage-seating's
                        // `prebookedTotal` override would turn it into an amount due.
                        //
                        // RELEASED: `nolSummaryRowsForTrip` drops an UNISSUED released pass (no row,
                        // no total), but an ISSUED one — a paid pass cancelled for nothing with its
                        // non-refundable ticket — draws a "(Cancelled)" row into refundedTotal, the
                        // same as REFUNDED. Dedup here is by offerId, so it is still counted once.
                        // It is admitted for symmetry with
                        // `isAdmissionKeptInPartialView`, which the kept-leg path applies — but
                        // note that path applies it ONLY when `isPartialPassengerView`, so on a
                        // whole-card view (MDU2FK's own shape) the kept legs get no status filter
                        // at all. That gap is the kept path's, pre-dating this clause; do not
                        // "restore consistency" by removing the filter here.
                        //
                        // Gated on `cancelledLegOffers` below, deliberately: the summary draws Nol
                        // rows per TRIP, and a trip only reaches `allTripWithoutExchange` through
                        // an offer's `coveredTripId` — which a pass offer has none of. Without the
                        // cancelled TRAIN offer there is no trip to hang the row on, so admitting
                        // the pass alone would carry money onto a page that cannot show it.
                        const cancelledTripLegIds = new Set<string>(
                            allTrips
                                .filter((trip: any) => cancelledLegs.includes(trip.id))
                                .flatMap((trip: any) => (trip.legs || []).map((leg: any) => leg.id))
                        );
                        const alreadyListed = new Set<string>(
                            [...filteredOffers, ...cancelledLegOffers].map((offer: any) => offer.offerId)
                        );
                        const cancelledNolOffers = nolOffersForLegs(
                            { bookedOffers: allBookedOffers },
                            cancelledTripLegIds
                        )
                            .filter((offer: any) => !alreadyListed.has(offer.offerId))
                            .map((offer: any) => ({
                                ...offer,
                                admissions: (offer.admissions || []).filter((admission: any) =>
                                    namesCardPassenger(admission?.passengerIds) &&
                                    PAID_OR_REFUNDED_ADMISSION_STATUSES.has(admission?.status)
                                ),
                            }))
                            .filter((offer: any) => (offer.admissions || []).length > 0);

                        const summaryBooking = cancelledLegOffers.length > 0
                            ? {
                                ...updatedBooking,
                                // allTrips order, so the summary reads legs in the order the
                                // backend sent them rather than kept-then-cancelled.
                                trips: allTrips.filter(
                                    (trip: any) => keptTripIds.includes(trip.id) || cancelledLegs.includes(trip.id)
                                ),
                                bookedOffers: [...filteredOffers, ...cancelledLegOffers, ...cancelledNolOffers],
                            }
                            : updatedBooking;

                        prepareBookingSummaryAction.setParams({ ...summaryBooking, isSplit: true, onlyFulfillPax: true, isManageSeating: isManageSeating, isFrom: "booking-details" });
                        await prepareBookingSummaryAction.execute();

                        if (shouldRequestUpgrade(updatedBookingResponse.booking) && !hasOnlyShuttleLegs(updatedBookingResponse.booking) && !areAllPassengersInLatestTripsAlreadyUpgraded(updatedBookingResponse.booking)) {
                            await requestUpgradeOffersForBooking(updatedBookingResponse.booking as any);
                        }
                    } else if (direction) {
                        const allTrips = bookingResponse.booking?.trips || [];
                        const allBookedOffers = bookingResponse.booking?.bookedOffers || [];
                        const allPassengers = bookingResponse.booking?.passengers || [];

                        // Check if this is a round-trip booking (has both outbound and inbound trips).
                        // Prefer trips with a TRAIN leg — a standalone onward-shuttle trip
                        // can share the same direction as the train trip and must not
                        // hijack this lookup (see preferTrainTrips doc).
                        const outboundTrip = preferTrainTrips(allTrips).find((trip: any) => trip.direction === TripTypeEnum.OUT_BOUND);
                        const inboundTrip = preferTrainTrips(allTrips).find((trip: any) => trip.direction === TripTypeEnum.IN_BOUND);
                        const isRoundTrip = outboundTrip && inboundTrip;

                        // Check if this is a split booking (one leg past, one leg future)
                        // For split bookings, we should respect the direction filter
                        let isSplitBooking = false;
                        if (isRoundTrip) {
                            // The appearance pivot, so this agrees with the list
                            // ON THE PIVOT — the list additionally refuses to
                            // split shuttle bookings at all, which this site has
                            // never mirrored (pre-existing divergence):
                            // the bookings row stays COMBINED for one period after
                            // the outbound leaves (the list-side split is pivoted
                            // too), and splitting here would have the details page
                            // treat as two journeys what the row the user just
                            // clicked presented as one.
                            //
                            // Asia/Dubai on BOTH sides, like the list-side split.
                            // This previously shifted only `now` by +4h while the
                            // trip times stayed absolute instants, which put the
                            // threshold four hours into the future — the same bug
                            // the sibling site fixed (its old line is still there,
                            // commented, above `// Fix: Use Asia/Dubai`).
                            const pivotDubai = bookingsPastPivotDubai();

                            if (outboundTrip.startTime && inboundTrip.startTime) {
                                const isOutboundPast = dayjs(outboundTrip.startTime).tz("Asia/Dubai").isBefore(pivotDubai);
                                const isInboundPast = dayjs(inboundTrip.startTime).tz("Asia/Dubai").isBefore(pivotDubai);
                                // Split booking: one is past and one is future
                                isSplitBooking = isOutboundPast !== isInboundPast;
                            }
                        }

                        // For round-trip bookings that are NOT split, show the full booking without filtering by direction
                        if (isRoundTrip && !isSplitBooking) {
                            updateRecoilState('bookingResponseAtom', bookingResponse);

                            if (shouldRequestUpgrade(bookingResponse.booking) &&
                                !hasOnlyShuttleLegs(bookingResponse.booking) &&
                                !areAllPassengersInLatestTripsAlreadyUpgraded(bookingResponse.booking)) {
                                await requestUpgradeOffersForBooking(bookingResponse.booking as any);
                            }
                            prepareBookingSummaryAction.setParams({ ...bookingResponse.booking, isManageSeating: isManageSeating, isFrom: "booking-details" });
                            await prepareBookingSummaryAction.execute();
                        } else {
                            // For one-way bookings, apply direction filtering
                            let directionToKeep = "";
                            let directionToRemove = "";

                            if (direction === TrainTypeEnum.outbound) {
                                directionToKeep = TripTypeEnum.OUT_BOUND;
                                directionToRemove = TripTypeEnum.IN_BOUND;
                            } else if (direction === TrainTypeEnum.inbound) {
                                directionToKeep = TripTypeEnum.IN_BOUND;
                                directionToRemove = TripTypeEnum.OUT_BOUND;
                            }

                            if (directionToKeep && directionToRemove) {
                                // Filter trips to keep only the desired direction
                                let keptTrips = allTrips.filter((trip: any) => trip.direction === directionToKeep);

                                // If multiple keptTrips, check for single-leg trips with inactive legs and remove them
                                if (keptTrips.length > 1) {
                                    keptTrips = keptTrips.filter((trip: any) => {
                                        if (Array.isArray(trip.legs) && trip.legs.length === 1) {
                                            return trip.legs[0]?.isActive === true;
                                        }
                                        return true; // keep trips with multiple legs or no legs
                                    });
                                }

                                // Identify IDs of removed trips (opposite direction)
                                const removedTripIds = allTrips
                                    .filter((trip: any) => trip.direction === directionToRemove)
                                    .map((trip: any) => trip.id);

                                // Filter bookedOffers to exclude those linked to removed trips
                                const filteredOffers = allBookedOffers.filter((offer: any) => {
                                    const coveredId = offer.tripCoverage?.coveredTripId;
                                    return !removedTripIds.includes(coveredId);
                                });

                                // If displayPassengerData is provided, filter passengers by the IDs in displayPassengerData
                                let filteredPassengers = allPassengers;
                                if (displayPassengerData && displayPassengerData.length > 0) {
                                    const displayPassengerIds = displayPassengerData.map((data: any) => data.id);
                                    filteredPassengers = allPassengers.filter((passenger) =>
                                        displayPassengerIds.includes(passenger.id)
                                    );
                                }

                                // The legs each kept trip superseded — the SECOND
                                // write site that needs the handoff, and the one the
                                // CANCEL flow reads.
                                //
                                // The direction filter above keeps every same-direction
                                // trip, so on an exchanged booking all of them survive
                                // it; the `isActive` filter then drops the exchanged-away
                                // ones (their single leg reads `isActive: false`). That
                                // leaves `trips` holding the live trip alone, exactly as
                                // the passenger-scoped site does — so a Nol pass naming
                                // an old leg is invisible to every reader of this atom.
                                //
                                // Verified on QGB756: trips 6aac6dab/486e505d carry
                                // `isActive: false`, 1f2e5b3b `true`, and the cancel
                                // flow's `/refund-offers` request carried the train
                                // admission and the meal ancillary and NO pass — the
                                // customer's AED 40 was silently kept.
                                //
                                // Candidates are narrowed to trips this booking actually
                                // EXCHANGED before pairing; `pairSupersededLegs` then
                                // matches on span, so a same-direction trip dropped for
                                // any other reason contributes nothing.
                                const supersededForDirection = supersededTripsOf(allTrips, keptTrips, allBookedOffers);

                                // Construct updated booking object
                                const updatedBooking: IBooking & IDisplaySupersededLegs = {
                                    ...bookingResponse.booking,
                                    trips: keptTrips,
                                    bookedOffers: filteredOffers,
                                    passengers: filteredPassengers,
                                    displaySupersededLegIdsByTripId: pairSupersededLegs(
                                        keptTrips,
                                        supersededForDirection
                                    ),
                                    displayIsBookingDetailsView: true
                                };

                                const updatedBookingResponse = {
                                    ...bookingResponse,
                                    booking: updatedBooking
                                };

                                // Update state and execute summary action
                                updateRecoilState('bookingResponseAtom', updatedBookingResponse);

                                prepareBookingSummaryAction.setParams({ ...updatedBooking, isSplit: true, isManageSeating: isManageSeating, isFrom: "booking-details" });
                                await prepareBookingSummaryAction.execute();

                                if (shouldRequestUpgrade(updatedBookingResponse.booking) && !hasOnlyShuttleLegs(updatedBookingResponse.booking)) {
                                    await requestUpgradeOffersForBooking(updatedBookingResponse.booking as any);
                                }
                            }
                        }
                    }
                    else {
                        updateRecoilState('bookingResponseAtom', bookingResponse);
                        const isChangeDateFlow = localStorage.getItem(STORAGE_KEYS.CHANGE_DATE_FLOW) || "false";
                        const trips = bookingResponse.booking?.trips || [];
                        const isOneWayTrip = trips.length > 0 && trips.every(trip => trip.direction === TripTypeEnum.OUT_BOUND);
                        
                        if (shouldRequestUpgrade(bookingResponse.booking) && 
                            !hasOnlyShuttleLegs(bookingResponse.booking) &&
                            !(isChangeDateFlow === "true" && isOneWayTrip) && 
                            !areAllPassengersInLatestTripsAlreadyUpgraded(bookingResponse.booking)) {
                            await requestUpgradeOffersForBooking(bookingResponse.booking as any);
                        }
                        prepareBookingSummaryAction.setParams({ ...bookingResponse.booking, isManageSeating: isManageSeating, isFrom: "booking-details" })
                        await prepareBookingSummaryAction.execute()

                        
                    }

                    /**
                     * This is only for when user try to do guest booking
                     * It will retrieve and update recoil to show booking on 
                     * Upcoming page and also whenever redirect to upcoming page.
                    */
                    if (isAnonymous) {
                        localStorage.setItem("retrieveBookingCacheData", JSON.stringify({
                            retrieveBooking: {
                                bookingId: bookingId,
                            },
                            reference: [],
                            lastName: []
                        }))
                    } else {
                        localStorage.removeItem("retrieveBookingCacheData")
                    }

                    /**
                     * This is to show newly added addons success message and
                     * show toast for removed addons.
                     *
                     * Gated on the sessionStorage flags ALONE. Three actions
                     * write them — PersistAddonsOnConfirmAction (the confirm),
                     * PaymentProcessAction and GuestPaymentProcessAction (the
                     * Pay click) — and every one of them already gates the write
                     * on its own manage-seating read, and mostly removes the key
                     * in the else (PersistAddonsOnConfirmAction's two removes are
                     * themselves gated on having something to delete/add, so a
                     * no-op confirm can leave an older key in place — harmless,
                     * since the value names the booking it was raised for). So
                     * the keys are only ever SET in a manage-seating context,
                     * and they are consumed unconditionally just below.
                     * The outer `isManageSeating` check was therefore redundant,
                     * and it read the leaked flow flag that
                     * navigateToBookingDetails now clears on payment success —
                     * which would have silenced both toasts on the very
                     * settled-payment path they exist for.
                     *
                     * The flag VALUE is the booking it was raised for, not "yes",
                     * so a key raised for one booking cannot toast on another.
                     * That is only half the guard: the confirm-time writer
                     * raises the key BEFORE any payment, and an id match does
                     * nothing about the same booking being reopened later. The
                     * removed `isManageSeating` wrapper had been suppressing
                     * that case too, so ResetManageSeatingStateAction now
                     * clears both keys on every flow teardown — which is where
                     * an abandoned confirm actually ends up.
                    */
                    const needToShowAddedAddons = sessionStorage.getItem("needToShowAddedAddons")
                    const needToShowRemovedAddons = sessionStorage.getItem("needToShowRemovedAddons")
                    if (needToShowAddedAddons === String(bookingId)) {
                        showNotification(
                            "success",
                            "Add-ons Added",
                            "New add-ons have been successfully added to your booking.",
                        );
                    }
                    if (needToShowRemovedAddons === String(bookingId)) {
                        showNotification(
                            "info",
                            "Add-ons Cancelled",
                            "Add-ons cancellation confirmed. You will not receive a refund for cancellations made less than 48 hours before departure.",
                        );
                    }
                    sessionStorage.removeItem("needToShowAddedAddons")
                    sessionStorage.removeItem("needToShowRemovedAddons")
                    updateRecoilState("isManageSeatingAtom", isManageSeating);

                    /**
                     * Price the train ticket of any leg holding a Nol pass —
                     * ONLY when there is one.
                     *
                     * A pass follows its ticket, so NolCardModal has to know
                     * BEFORE it paints whether that ticket refunds anything. The
                     * only source is POST /refund-offers: the booking cannot
                     * answer it (`refundAmount` is null on every FULFILLED part,
                     * and `refundable: "YES"` reads the same on a Saver whose fee
                     * eats the whole fare). Quoting on modal-open instead would
                     * make the control appear or vanish a beat after the modal
                     * does; the page load gives the answer a head start no click
                     * can outrun.
                     *
                     * GATED, because this page made no refund-offers request at
                     * all before. Unconditional, every read-only view of any
                     * booking holding a meal would POST one and leave PROPOSED
                     * offers behind for a cache nothing on this page reads —
                     * `PaymentReceipt` gates its use on `screen !== "details"`,
                     * and `PrepareBookingSummaryAction` forces the pending queue
                     * to null here. Only a booking with a paid pass has a
                     * consumer.
                     *
                     * KNOWN CEILING, both directions: the action always sweeps
                     * ancillaries, so a Nol booking with meals sends a second,
                     * unconsumed batch HERE — and it always sweeps tickets, so
                     * MANAGE-SEATING entry on a Nol booking now proposes refund
                     * offers against train admissions that nothing on that
                     * surface reads. Both are bounded (Nol bookings only) and
                     * neither settles anything, so they do not buy a
                     * `ticketsOnly` param's worth of complexity in a shared
                     * action. Add the param if that stops being true — and
                     * re-check it if PROPOSED offers ever start interfering with
                     * a pending exchange, the way a booked offer does.
                     *
                     * Safe to fire speculatively: /refund-offers only PROPOSES
                     * (status PROPOSED, validUntil +15 min); nothing settles
                     * without the PATCH. Same property
                     * ManageSeatingRedirectClickAction already relies on.
                     *
                     * NOT awaited, and never thrown from: the cache is an
                     * optimisation and a failed probe must leave the control
                     * VISIBLE (nolRefundBlockedByTicket reads "no quote" as "not
                     * blocked"), not block the page. `deduplicate = true` means a
                     * probe still in flight for a PREVIOUS booking swallows this
                     * one — the readers all guard on bookingId, so that shows the
                     * control rather than hiding it wrongly.
                     */
                    if (
                        ticketFulfillmentIdsForNolLegs(
                            getRecoilStateValue<IBookingResponse>("bookingResponseAtom") ?? null,
                        ).length > 0
                    ) {
                        void probeRefundOffersAction.execute();
                    }
                } else {
                    console.warn("Failed to fetch Data.");
                }


                /**
                 * Get list of service request for this booking
                */
                await getListOfServiceRequestAction.setParams({ bookingId })
                await getListOfServiceRequestAction.execute()
                if (isActionRunSuccessfully(getListOfServiceRequestAction)) {
                    const result = getListOfServiceRequestAction?.result
                    if (result?.status === "success") {

                    }
                }
            }
        } catch (error: any) {
            console.error("Error fetching Data:", error);
        }
    }
}

export const getBookingDetailDataAction = new GetBookingDetailDataAction()